// Utility functions for formatting numbers and currency

export const formatNumber = (num: number, decimals: number = 0): string => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  
  return num.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatCurrency = (amount: number, currency: string = 'INR'): string => {
  if (amount === null || amount === undefined || isNaN(amount)) return '₹0';
  
  if (currency === 'INR') {
    return `₹${formatNumber(amount, 0)}`;
  }
  
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const formatPercentage = (value: number, decimals: number = 1): string => {
  if (value === null || value === undefined || isNaN(value)) return '0%';
  
  return `${formatNumber(value, decimals)}%`;
};

export const formatDecimal = (value: number, decimals: number = 2): string => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  
  return formatNumber(value, decimals);
};

export const formatCompactNumber = (num: number): string => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  
  if (num >= 10000000) { // 1 crore
    return `${formatNumber(num / 10000000, 1)}Cr`;
  } else if (num >= 100000) { // 1 lakh
    return `${formatNumber(num / 100000, 1)}L`;
  } else if (num >= 1000) { // 1 thousand
    return `${formatNumber(num / 1000, 1)}K`;
  }
  
  return formatNumber(num);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const remainingMinutes = Math.floor((seconds % 3600) / 60);
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }
};

export const formatDistance = (km: number, unit: 'km' | 'miles' = 'km'): string => {
  if (km === null || km === undefined || isNaN(km)) return '0 km';
  
  if (unit === 'miles') {
    const miles = km * 0.621371;
    return `${formatNumber(miles, 1)} miles`;
  }
  
  return `${formatNumber(km, 1)} km`;
};

export const formatFuelEfficiency = (kmpl: number, unit: 'kmpl' | 'mpg' = 'kmpl'): string => {
  if (kmpl === null || kmpl === undefined || isNaN(kmpl)) return '0 km/l';
  
  if (unit === 'mpg') {
    const mpg = kmpl * 2.35214;
    return `${formatNumber(mpg, 1)} mpg`;
  }
  
  return `${formatNumber(kmpl, 1)} km/l`;
};
